import { ReactNode, RefObject } from 'react';
import { IconType } from 'react-icons';
import type { ButtonProps as ChakraButtonProps, IconButtonProps as ChakraIconButtonProps } from '@chakra-ui/react';
import { AbsoluteCenter, Button as ChakraButton, Icon, Span, Spinner } from '@chakra-ui/react';
import { RequireExactlyOne } from 'type-fest';

import { LEGACY_MODE, useMode } from '../Mode';

interface ButtonLoadingProps {
  loading?: boolean;
  loadingText?: ReactNode;
}

interface ButtonIconProps {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export interface ButtonProps extends ChakraButtonProps, ButtonLoadingProps, ButtonIconProps {
  ref?: RefObject<HTMLButtonElement | null>;
}

export const BUTTON_ICON_SIZE = 6;

export function Button(props: ButtonProps) {
  const { loading, disabled, loadingText, children, rightIcon, leftIcon, ...rest } = props;
  const buttonContent = (
    <>
      {leftIcon}
      {children}
      {rightIcon}
    </>
  );
  return (
    <ChakraButton disabled={loading || disabled} {...rest}>
      {loading && !loadingText ? (
        <>
          <AbsoluteCenter display="inline-flex">
            <Spinner size="inherit" color="inherit" />
          </AbsoluteCenter>
          <Span opacity={0}>{buttonContent}</Span>
        </>
      ) : loading && loadingText ? (
        <>
          <Spinner size="inherit" color="inherit" />
          {loadingText}
        </>
      ) : (
        buttonContent
      )}
    </ChakraButton>
  );
}

export type IconButtonProps = RequireExactlyOne<
  ChakraIconButtonProps & {
    icon: IconType;
  },
  'icon' | 'children'
> & { ref?: RefObject<HTMLButtonElement | null> };
export function IconButton({ icon, ref, ...props }: IconButtonProps) {
  const { mode } = useMode();
  return (
    <Button ref={ref} padding={0} {...props}>
      {props.children ? (
        props.children
      ) : (
        <Icon as={icon} boxSize={mode === LEGACY_MODE ? undefined : BUTTON_ICON_SIZE} />
      )}
    </Button>
  );
}
